import { create } from 'zustand'
import { supabase } from './supabase'

export const useAuthStore = create((set, get) => ({
  user: null,
  profile: null,
  session: null,
  loading: true,
  initialized: false,
  sessionExpired: false,
  isLoggingOut: false,

  // Initialize auth listener
  initialize: async () => {
    // Get initial session
    const { data: { session } } = await supabase.auth.getSession()

    if (session?.user) {
      const profile = await get().fetchProfile(session.user.id)
      set({ user: session.user, session, profile, loading: false, initialized: true })
    } else {
      set({ user: null, session: null, profile: null, loading: false, initialized: true })
    }

    // Listen for auth changes
    supabase.auth.onAuthStateChange(async (event, session) => {
      if (session?.user) {
        const profile = await get().fetchProfile(session.user.id)
        set({ user: session.user, session, profile, loading: false, sessionExpired: false })
      } else {
        const wasLoggedIn = get().user !== null
        const isLoggingOut = get().isLoggingOut
        set({ user: null, session: null, profile: null, loading: false })
        if (wasLoggedIn && event === 'SIGNED_OUT' && !isLoggingOut) {
          set({ sessionExpired: true })
        }
      }
    })
  },

  clearSessionExpired: () => set({ sessionExpired: false }),

  // Fetch user profile from public.users table
  fetchProfile: async (userId) => {
    try {
      const { data, error } = await supabase
        .from('users')
        .select('*')
        .eq('id', userId)
        .single()

      if (error) {
        console.error('Error fetching profile:', error)
        return null
      }
      return data
    } catch (err) {
      console.error('Profile fetch failed:', err)
      return null
    }
  },

  // Register new user (mirrors Alpha1 register flow)
  register: async ({ email, password, username, firstName, lastName, city, state, termsAccepted }) => {
    // Validate required fields
    if (!email || !password || !username) {
      throw new Error('Email, username, and password required')
    }
    if (!termsAccepted) {
      throw new Error('You must accept the Terms of Service to register.')
    }

    // Normalize username
    const usernameClean = username.trim().toLowerCase()

    if (!/^[a-z0-9._]+$/.test(usernameClean)) {
      throw new Error('Username can only contain letters, numbers, dots, and underscores.')
    }

    if (usernameClean.includes('@')) {
      throw new Error('Username cannot be an email address. This is for your @hiremail.net alias.')
    }

    // Banned keywords check (mirrors Alpha1)
    const BANNED_KEYWORDS = ['admin', 'support', 'scamview', 'hiremail', 'mod', 'staff']
    const isReserved = BANNED_KEYWORDS.some(word => usernameClean.includes(word))
    if (isReserved) {
      throw new Error('Username contains inappropriate or reserved terms.')
    }

    // Check if username is taken
    const { data: existingUser } = await supabase
      .from('users')
      .select('id')
      .eq('username', usernameClean)
      .single()

    if (existingUser) {
      throw new Error('Username already taken')
    }

    // Check if username is taken by an alias
    const { data: existingAlias } = await supabase
      .from('user_aliases')
      .select('id')
      .eq('alias_local', usernameClean)
      .single()

    if (existingAlias) {
      throw new Error('Username already taken (reserved by alias)')
    }

    // Sign up with Supabase Auth
    const { data: authData, error: authError } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          username: usernameClean,
          first_name: firstName || '',
          last_name: lastName || '',
          city: city || '',
          state: state || ''
        },
        emailRedirectTo: `${window.location.origin}/verify-email`
      }
    })

    if (authError) {
      if (authError.message.includes('already registered')) {
        throw new Error('Email already exists')
      }
      throw new Error(authError.message)
    }

    // Create user profile in public.users table
    // The 14-day trial is set by DEFAULT in the database schema (trial_ends_at = NOW() + 14 days)
    if (authData.user) {
      const { error: profileError } = await supabase
        .from('users')
        .insert({
          id: authData.user.id,
          email: email,
          username: usernameClean,
          first_name: firstName || null,
          last_name: lastName || null,
          city: city || null,
          state: state || null,
          subscription_status: 'trial',
          is_verified: false,
          terms_accepted_at: termsAccepted ? new Date().toISOString() : null
        })

      if (profileError) {
        console.error('Profile creation error:', profileError)
        // Don't throw - the auth user was created, profile will be created by trigger fallback
      }

      // Create HireMail alias (mirrors Alpha1)
      const { error: aliasError } = await supabase
        .from('user_aliases')
        .insert({
          user_id: authData.user.id,
          alias_local: usernameClean,
          destination_email: email,
          is_active: true
        })

      if (aliasError) {
        console.error('Alias creation error:', aliasError)
      }
    }

    return authData
  },

  // Sign in with Google OAuth
  signInWithGoogle: async () => {
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: `${window.location.origin}/auth/callback`
      }
    })
    if (error) throw new Error(error.message)
  },

  // Complete registration for an OAuth user who already has an auth account
  // (no email/password signup — just create profile + alias)
  registerOAuth: async ({ username, termsAccepted }) => {
    const userId = get().user?.id
    const email = get().user?.email

    if (!userId) throw new Error('Not authenticated')
    if (!username) throw new Error('Username required')
    if (!termsAccepted) throw new Error('You must accept the Terms of Service to register.')

    const usernameClean = username.trim().toLowerCase()

    if (!/^[a-z0-9._]+$/.test(usernameClean)) {
      throw new Error('Username can only contain letters, numbers, dots, and underscores.')
    }
    if (usernameClean.includes('@')) {
      throw new Error('Username cannot be an email address.')
    }

    const BANNED_KEYWORDS = ['admin', 'support', 'scamview', 'hiremail', 'mod', 'staff']
    if (BANNED_KEYWORDS.some(w => usernameClean.includes(w))) {
      throw new Error('Username contains reserved terms.')
    }

    const { data: existingUser } = await supabase
      .from('users').select('id').eq('username', usernameClean).single()
    if (existingUser) throw new Error('Username already taken')

    const { data: existingAlias } = await supabase
      .from('user_aliases').select('id').eq('alias_local', usernameClean).single()
    if (existingAlias) throw new Error('Username already taken')

    const { error: profileError } = await supabase
      .from('users')
      .insert({
        id: userId,
        email,
        username: usernameClean,
        subscription_status: 'trial',
        is_verified: true,
        terms_accepted_at: new Date().toISOString()
      })
    if (profileError) throw new Error(profileError.message)

    await supabase
      .from('user_aliases')
      .insert({ user_id: userId, alias_local: usernameClean, destination_email: email, is_active: true })

    const profile = await get().fetchProfile(userId)
    set({ profile })
  },

  // Login (mirrors Alpha1 login flow)
  login: async (email, password) => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password
    })

    if (error) {
      if (error.message.includes('Email not confirmed')) {
        throw new Error('Email not verified. Please check your inbox for the verification link.')
      }
      if (error.message.includes('Invalid login credentials')) {
        throw new Error('Invalid email or password')
      }
      throw new Error(error.message)
    }

    // Fetch profile to get subscription info
    if (data.user) {
      const profile = await get().fetchProfile(data.user.id)
      set({ user: data.user, session: data.session, profile })
    }

    return data
  },

  // Logout
  logout: async () => {
    set({ isLoggingOut: true })
    const { error } = await supabase.auth.signOut()
    if (error) { set({ isLoggingOut: false }); throw new Error(error.message) }
    set({ user: null, session: null, profile: null, isLoggingOut: false })
  },

  // Forgot password (mirrors Alpha1)
  forgotPassword: async (email) => {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`
    })
    if (error) throw new Error(error.message)
  },

  // Reset password (mirrors Alpha1)
  resetPassword: async (newPassword) => {
    try {
      const { data, error } = await supabase.auth.updateUser({
        password: newPassword
      })
      if (error) {
        console.error('Supabase updateUser error:', error)
        throw new Error(error.message)
      }
      return data
    } catch (err) {
      console.error('resetPassword failed:', err)
      throw err
    }
  },

  // Resend verification email
  resendVerification: async (email) => {
    const { error } = await supabase.auth.resend({
      type: 'signup',
      email
    })
    if (error) throw new Error(error.message)
  },

  // Update profile (mirrors Alpha1 PUT /profile)
  updateProfile: async ({ username, password }) => {
    const userId = get().user?.id
    if (!userId) throw new Error('Not authenticated')

    if (username) {
      const clean = username.trim().toLowerCase()
      if (!/^[a-z0-9._]+$/.test(clean)) {
        throw new Error('Invalid username format')
      }
      if (clean.includes('@')) {
        throw new Error('Username cannot be an email address.')
      }

      const BANNED_KEYWORDS = ['admin', 'support', 'scamview', 'hiremail', 'mod', 'staff']
      if (BANNED_KEYWORDS.some(word => clean.includes(word))) {
        throw new Error('Username contains inappropriate or reserved terms.')
      }

      // Update username in users table
      const { error } = await supabase
        .from('users')
        .update({ username: clean })
        .eq('id', userId)

      if (error) {
        if (error.message.includes('unique') || error.message.includes('duplicate')) {
          throw new Error('Username already taken')
        }
        throw new Error(error.message)
      }

      // Update alias to match
      await supabase
        .from('user_aliases')
        .update({ alias_local: clean })
        .eq('user_id', userId)
    }

    if (password) {
      const { error } = await supabase.auth.updateUser({ password })
      if (error) throw new Error(error.message)
    }

    // Refresh profile
    const profile = await get().fetchProfile(userId)
    set({ profile })
  },

  // Refresh profile from DB (call after payment success, cancellation, etc.)
  refreshProfile: async () => {
    const userId = get().user?.id
    if (!userId) return null
    const profile = await get().fetchProfile(userId)
    set({ profile })
    return profile
  },

  // Get subscription info (mirrors Alpha1 checkSubscription middleware)
  getSubscriptionInfo: () => {
    const profile = get().profile
    if (!profile) return { plan: 'none', isPremium: false, isTrial: false, daysRemaining: 0, features: {} }

    const now = new Date()
    let isTrial = false
    let daysRemaining = 0

    if (profile.trial_ends_at) {
      const trialEnd = new Date(profile.trial_ends_at)
      const diffTime = trialEnd - now
      if (diffTime > 0) {
        isTrial = true
        daysRemaining = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
      }
    }

    const plan = profile.subscription_status || 'trial'
    const isPremium = plan === 'premium' || isTrial

    return {
      plan,
      isTrial,
      daysRemaining,
      isPremium,
      isCancelled: plan === 'cancelled',
      features: {
        aiAnalysis: isPremium,
        brandCheck: isPremium,
        phoneLookup: isPremium,
        linkMasking: isPremium,
        communityReviews: isPremium
      }
    }
  },

  // Get credit balance from profile data
  getCreditsInfo: () => {
    const profile = get().profile
    if (!profile) return { monthly: 0, purchased: 0, total: 0, planAllocation: 0 }

    const PLAN_CREDITS = { trial: 500, basic: 300, premium: 500 }
    const plan = profile.subscription_status || 'basic'
    const planAllocation = PLAN_CREDITS[plan] || 0

    return {
      monthly: profile.monthly_credits || 0,
      purchased: profile.purchased_credits || 0,
      total: (profile.monthly_credits || 0) + (profile.purchased_credits || 0),
      planAllocation
    }
  },

  // Update credits in store from scan response (avoids full profile refetch)
  updateCreditsFromScan: (creditsData) => {
    if (!creditsData) return
    const profile = get().profile
    if (!profile) return
    set({
      profile: {
        ...profile,
        monthly_credits: creditsData.monthly,
        purchased_credits: creditsData.purchased
      }
    })
  }
}))
